import { cond, T, pipe, pathOr, compose, head, lensPath, set, equals, filter, flatten, always, not } from 'ramda';
import { isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import { mkAPIUpdateableForArrayType } from 'src/features/core/ChatEngine/Bot/botServiceUtils/arrayTypeHandler';
import {
  shouldAPIToBeUpdated,
  dataFeederForCommonDataType,
  updateReducers,
  getActiveContext
} from 'src/features/core/ChatEngine/Bot/botServiceUtils/commonFns';
import { mkAPIUpdateableForPathType } from 'src/features/core/ChatEngine/Bot/botServiceUtils/pathTypeHandler';
import { wipeOutDependentAnswer } from 'src/features/core/ChatEngine/Bot/botServiceUtils/intentRemoveHandler';
import { currentAppSession } from "src/utilities/storage";
import { describeLoanStatus } from 'src/services/loanStatus';
import { updateLoanStatus } from 'src/store/loanStatus/loan_status.requests';
import { mkBuildLoanStage } from 'src/services/objects/loanStatus';
import { mkAPIContextResource } from "src/features/core/Intents/ApiContext/apiContextResourceCreator";
import { LENDSMART_WITHOUT_BOT } from 'src/services/constants/activites';
import { notHasWorkflowLoanActive } from 'src/features/utilities';
import { executeEventContextRunner } from '../Engine/rawEventContext';
import { reportException } from 'src/exceptionReporting';
import { ReduxStateStore, SessionStateStore } from '../Configs/apiResouces';
import { executeAuditActivityRunner } from '../Engine/auditActivityContext';
import { APIResourceValueEvaluator } from '../Configs/apiResourceFields';
import { ServiceAPIBuilder } from 'src/services/serviceBuilder';
import {
  ApiResource,
  API_RESOURCE_WORKFLOW_INTENTS,
  API_RESOURCE_ASSETS,
  API_RESOURCE_BORROWERS,
  API_RESOURCE_BUSINESS,
  API_RESOURCE_DECLARATIONS,
  API_RESOURCE_EMPLOYMENTS,
  API_RESOURCE_LENDING_TERMS,
  API_RESOURCE_LIABILITIES,
  API_RESOURCE_MONTHLY_INCOMES,
  API_RESOURCE_PROPERTIES,
  API_RESOURCE_REAL_ESTATE_ASSET,
  API_RESOURCE_USER_ACCOUNTS,
  META_DATA
} from 'src/services/constants';

//////////////////  PUBLIC FUNCTIONS ////////////////////////

/**
 * updateFn - this method will update the neccessary data to its api resource that is configured by site intent
 * for some intents like upload we not need api resource update in that we can use lensmart_sh/is_api_update to false
 * where update will not happend only intent will update,
 * if lendsmart_sh/is_api_update is not given then it will take it as update the data
 * @param answer can have a answer
 * @param userIntent can have a user intent
 * @param singleSiteIntent - can have a single site intent configured intent
 * @param siteIntents - can have a overall site intent
 * @param _apiResourceData - can have api resource needed for this fucntions
 * @param eventContext - can have souurce ,action and payload to be pushing an event
 * @param statusContext - can have loan status to be updated
 * @param rawEventContext - can have souurce ,action and payload to be pushing an event using graphql
 *
 */
/**TO-DO: need to add interface here */
export const updateIntentApiResourceFn = async (
  dispatch: any,
  answer: any,
  userSpecficIntent: any,
  singleSiteIntent: any,
  siteIntents: any,
  _apiResourceData: any,
  intentWithAnswerFn: any,
  updateWorkflowLoading: any
) => {
  updateWorkflowLoading(true)
  const userIntents = pathOr([], ['intents'], userSpecficIntent)
  const { loanAppId, loanRoleId, loanType } = currentAppSession();

  const getContext = (key: string) => pathOr([], [key], singleSiteIntent);

  try {
    const hasSubmitEnabled = isNotEmpty(pathOr('', [META_DATA, 'submit'], singleSiteIntent))
    const apiContexts = getContext('api_context');
    const statusContext = getActiveContext(getContext('status_context'), userIntents);
    const auditActivityContext = getContext('audit_activity_context');


    if (notHasWorkflowLoanActive()) {
      const services = mkAPIContextResource(
        {
          apiContexts,
          answer,
          dispatch,
          apiResources: new SessionStateStore(),
          userIntents,
          currentIntent: singleSiteIntent

        });
      const intentServiceData = {
        kind: API_RESOURCE_WORKFLOW_INTENTS,
        payload: intentWithAnswerFn()
      }
      const serviceBuilder = new ServiceAPIBuilder('', [...services, intentServiceData])
      await serviceBuilder.requestRedux(dispatch)
      return answer;
    }

    const workflowIntentServices = [{
      kind: API_RESOURCE_WORKFLOW_INTENTS as ApiResource,
      payload: intentWithAnswerFn()
    }]

    //TO-DO:we need to get rid of this metadata resource update and use api_context update
    const metadataServices = await updateApiResourceFn(
      dispatch,
      answer,
      userSpecficIntent,
      singleSiteIntent,
      _apiResourceData,
      siteIntents,
      _apiResourceData,
    )



    if (isNotEmpty(statusContext) && not(hasSubmitEnabled)) {
      const statusTobeUpdated = pathOr('', ['loan_status'], statusContext);
      describeLoanStatus(loanAppId).then(async (res: any) => {
        const loanStatus = head(pathOr('', ['items'], res));
        const loanStatusCondition = mkBuildLoanStage(
          statusTobeUpdated,
          pathOr('', ['status', 'conditions'], loanStatus),
          LENDSMART_WITHOUT_BOT,
          statusTobeUpdated
        );
        const loanStatusUpdatedData = compose(
          set(lensPath(['status', 'phase']), statusTobeUpdated),
          set(lensPath(['status', 'conditions']), loanStatusCondition)
        )(loanStatus);
        await dispatch(updateLoanStatus(loanStatusUpdatedData));
      });
    }

    const store = new ReduxStateStore(singleSiteIntent)
    await auditActivityRunner({ auditActivityContext, loanRoleId, loanAppId, loanType, store, userIntents, answer })

    const apiContextServices = mkAPIContextResource({
      apiContexts,
      answer,
      dispatch,
      apiResources: store,
      userIntents,
      currentIntent: singleSiteIntent,
    })

    const services = filter(isNotEmpty, flatten([metadataServices, apiContextServices, workflowIntentServices]))
    const serviceBuilder = new ServiceAPIBuilder(loanAppId, services)

    await serviceBuilder.requestRedux(dispatch)
    return answer;
  } catch (e) {
    reportException(`<---------SimpleBotApiService-139------------> ${e}`)
  }
  finally {
    executeEventContextRunner(getContext('event_context'), userIntents)
  }
};
/**
 * 
 * @param data arguments for running audit activity runner
 * This method will run audit acitivity if its  configured on intent
 */
export const auditActivityRunner = async (data: any) => {
  const { auditActivityContext, answer, loanAppId, loanType, loanRoleId, store, userIntents } = data;
  if (isNotEmpty(auditActivityContext)) {
    const dynamicFields = {
      '___this_answer___': answer
    }
    await executeAuditActivityRunner(
      auditActivityContext,
      loanAppId,
      loanType,
      loanRoleId,
      new APIResourceValueEvaluator(
        store,
        dynamicFields
      ),
      userIntents
    )
  }
  return

}

/**
 * updateApiResourceFn - basically this method will update the api resouce that is been configured by site intent
 * @param answer can have a answer
 * @param userIntent can have a user intent
 * @param singleSiteIntent can have a single site intent configured intent
 * @param _apiResourceData can have api resource needed for this fucntions
 * @param siteIntents can have a overall site intent
 */
export const updateApiResourceFn = (
  dispatch: any,
  answer: any,
  userSpecficIntent: any,
  singleSiteIntent: any,
  _apiResourceData: any,
  siteIntents: any,
  storeState: any,
) => {
  if (shouldAPIToBeUpdated(singleSiteIntent)) {
    const {
      apiResource,
      apiData,
      requests,
      filterArrayTypeData,
      filterPathTypeData,
      currenQuestionId,
      _apiResource,
      mappingKeys,
    } = dataFeederForCommonDataType(_apiResourceData, singleSiteIntent);

    return saveDataInApiForDataType({
      dispatch,
      apiResource,
      apiData,
      requests,
      singleSiteIntent,
      filterArrayTypeData,
      filterPathTypeData,
      answer,
      userIntent: userSpecficIntent,
      siteIntents,
      storeState,
      currenQuestionId,
      _apiResource,
      mappingKeys,
      _questionId: currenQuestionId,
      _apiResourceData,
    });
  }
  return []
};


/**
 * getUpdateFnForApiResource - this will return api resource supported name of PATCH update
 * @param apiResource this will which api resource the data will be updated
 */
// getForApiResource change to this
export const getForApiResource = (apiResource: any) => {
  return cond([
    [equals('business'), always(API_RESOURCE_BUSINESS)],
    [equals('borrower'), always(API_RESOURCE_BORROWERS)],
    [equals('property'), always(API_RESOURCE_PROPERTIES)],
    [equals('declaration'), always(API_RESOURCE_DECLARATIONS)],
    [equals('lendingTerm'), always(API_RESOURCE_LENDING_TERMS)],
    [equals('employment'), always(API_RESOURCE_EMPLOYMENTS)],
    [equals('monthlyIncome'), always(API_RESOURCE_MONTHLY_INCOMES)],
    [equals('realEstateAssets'), always(API_RESOURCE_REAL_ESTATE_ASSET)],
    [equals('accounts'), always(API_RESOURCE_USER_ACCOUNTS)],
    [equals('assets'), always(API_RESOURCE_ASSETS)],
    [equals('liabilities'), always(API_RESOURCE_LIABILITIES)],
    [T, always(apiResource)]
  ])(apiResource);
};


// const { requests, apiData, dispatch } = dataBag;
// const unifiedRequest = uniq(requests);
// return Promise.all(
//   unifiedRequest &&
//   unifiedRequest.map(
// );

const mkServiceRequests = (dataBag: any) => {

  const { apiResource, _apiResource, apiData } = dataBag;
  const apiResourceData = compose(set(lensPath([apiResource]), apiData))(
    _apiResource
  )

  const payload = pathOr({}, [apiResource], apiResourceData)
  const kind = getForApiResource(apiResource)

  return returnIsNotEmptyOrTrue(
    payload,
    [{
      kind,
      payload
    }],
    []
  )

}

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * lastUpdateCallForDataType - this will be update call using data type ,as of now path and array type is supported
 * @param arrayData array data is save in api field get filterd for array type update data
 * @param questionId can have a question id
 * @param uniqKeyMappingArray - its a uniq mapped key which will used for mapping into same object for sam key
 */
const saveDataInApiForDataType = (dataBag: any) => {
  const { filterArrayTypeData, singleSiteIntent = {} } = dataBag;
  const should_invalidate_and_save = pathOr('', ['should_invalidate_and_save'], singleSiteIntent)

  return cond([
    [
      () => equals(should_invalidate_and_save, 'true'),
      () => updateReducers(dataBag).then((res: any) => {
        dataBag.apiData = pathOr({}, ['data', 'items', 0], head(res))
        return pipe(
          wipeOutDependentAnswer,
          mkAPIUpdateableForPathType,
          mkServiceRequests
        )(dataBag)
      }),
    ],

    [
      isNotEmpty,
      () =>
        pipe(
          wipeOutDependentAnswer,
          mkAPIUpdateableForArrayType,
          mkServiceRequests
        )(dataBag),
    ],
    [
      T,
      () =>
        pipe(
          wipeOutDependentAnswer,
          mkAPIUpdateableForPathType,
          mkServiceRequests
        )(dataBag),
    ],
  ])(filterArrayTypeData);
};
